import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import StageFrame from './StageFrame';
import { useCycle } from './useCycle';

const list = (value: unknown): string[] => (Array.isArray(value) ? (value as string[]) : []);

const RoutingFlow: React.FC = () => {
  const { t } = useTranslation();
  const { phase, reduce, replay, run } = useCycle(6400);
  const chaos = list(t('anim.routing.chaos', { returnObjects: true }));
  const steps = list(t('anim.routing.steps', { returnObjects: true }));
  const problemCaption = t('anim.routing.problemCaption');
  const solutionCaption = t('anim.routing.solutionCaption');

  const problem = (
    <ul className="grid gap-3 sm:grid-cols-2">
      {chaos.map((item, index) => (
        <motion.li
          key={item}
          className="rounded-xl border border-coral/35 bg-coral-dim px-3 py-3 text-sm text-[#ffd0d8]"
          initial={reduce ? false : { opacity: 0, y: 16, rotate: index % 2 === 0 ? -4 : 3 }}
          animate={reduce ? undefined : { opacity: 1, y: [0, -6, 0], rotate: index % 2 === 0 ? -2 : 2 }}
          transition={reduce ? undefined : { delay: index * 0.08, y: { repeat: Infinity, duration: 2.4, delay: index * 0.15 } }}
        >
          {item}
        </motion.li>
      ))}
    </ul>
  );

  const solution = (
    <div className="relative pt-2">
      <div className="absolute left-[8%] right-[8%] top-[1.35rem] h-px bg-mint/40" aria-hidden="true" />
      <motion.div
        key={run}
        className="absolute left-[8%] top-[1.35rem] h-px origin-left bg-mint"
        initial={reduce ? false : { scaleX: 0 }}
        animate={{ scaleX: 1 }}
        transition={{ duration: reduce ? 0 : 0.9, ease: [0.22, 1, 0.36, 1] }}
        style={{ width: '84%' }}
        aria-hidden="true"
      />
      <ol className="relative grid grid-cols-5 gap-1.5 sm:gap-3">
        {steps.map((step, index) => (
          <motion.li
            key={step}
            className="flex flex-col items-center text-center"
            initial={reduce ? false : { opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: reduce ? 0 : 0.15 + index * 0.16 }}
          >
            <span className="z-10 flex h-7 w-7 items-center justify-center rounded-full border border-mint/50 bg-ink font-mono text-[0.65rem] text-mint">
              {index + 1}
            </span>
            <span className="mt-2 text-[0.7rem] font-medium leading-tight text-paper sm:text-sm">{step}</span>
          </motion.li>
        ))}
      </ol>
    </div>
  );

  return (
    <StageFrame
      kicker={t('anim.routing.kicker')}
      phase={reduce ? 'solution' : phase}
      caption={`${problemCaption} ${solutionCaption}`}
      onReplay={replay}
    >
      {reduce ? (
        <div className="space-y-6">
          {problem}
          {solution}
        </div>
      ) : phase === 'problem' ? (
        problem
      ) : (
        solution
      )}
    </StageFrame>
  );
};

export default RoutingFlow;
